import {
  LitElement,
  classMap,
  createRef,
  css,
  html,
  ref,
  viewportObserver,
} from '@hh/global.js'
import '@vendor/media-chrome'
import '@vendor/media-chrome-vimeo'
import '@vendor/media-chrome-youtube'

class HHMediaChromeVideo extends LitElement {
  static properties = {
    sectionId: { type: String, attribute: 'section-id' },
    srcDesktop: { type: String, attribute: 'src-desktop' },
    aspectRatioDesktop: { type: String, attribute: 'aspect-ratio-desktop' },
    srcMobile: { type: String, attribute: 'src-mobile' },
    aspectRatioMobile: { type: String, attribute: 'aspect-ratio-mobile' },
    srcType: { type: String, attribute: 'src-type' },
    posterDesktop: { type: String, attribute: 'poster-desktop' },
    posterMobile: { type: String, attribute: 'poster-mobile' },
    loadingType: { type: String, attribute: 'loading' },
    viewport: { type: String, state: true },
    autoplay: { type: Boolean },
    loop: { type: Boolean },
    sound: { type: Boolean },
    controls: { type: Boolean },
    captions: { type: String, attribute: 'captions' },
    trackProgress: { type: Boolean, attribute: 'track-progress' },
    background: { type: Boolean, attribute: 'background' },
    adaptContainerHeight: { type: Boolean, attribute: 'adapt-container-height' },
    title: { type: String, attribute: 'title' },
    description: { type: String, attribute: 'description' },
    playStatus: { type: Boolean },
  }

  get activeSrc() {
    if (!this.srcMobile) return this.srcDesktop
    return ['mobile', 'tablet'].includes(this.viewport) ? this.srcMobile : this.srcDesktop
  }

  get activeAspectRatio() {
    if (!this.aspectRatioMobile) return this.aspectRatioDesktop
    return ['mobile', 'tablet'].includes(this.viewport) ? this.aspectRatioMobile : this.aspectRatioDesktop
  }

  get activePoster() {
    return this.activeSrc === this.srcMobile && this.posterMobile !== 'false'
      ? this.posterMobile
      : this.activeSrc === this.srcDesktop && this.posterDesktop !== 'false'
        ? this.posterDesktop
        : false
  }

  constructor() {
    super()
    this.viewport = viewportObserver.currentViewport
    viewportObserver.addListener(this._handleViewportChange)
    this.srcMobile = false
    this.srcDesktop = false
    this.aspectRatioMobile = false
    this.aspectRatioDesktop = '16/9'
    this.videoRef = createRef()
    this.loadingType = 'lazy'
    this.background = false
    this.adaptContainerHeight = false
    this.playStatus = false
  }

  connectedCallback() {
    super.connectedCallback()
    if (this.autoplay) this.playStatus = true
    this.percentageCompleted = 0
    this.videoTwentyFive = false
    this.videoFifty = false
    this.videoSeventyFive = false
    this.videoComplete = false
    this.videoInView = false
  }

  firstUpdated() {
    this.$video = this.videoRef.value
  }

  _handleViewportChange = (newViewport) => {
    this.viewport = newViewport
  }

  _handlePlay = () => {
    this.playStatus = true
  }

  _handlePause = () => {
    this.playStatus = false
  }

  _handleProgress = () => {
    if (this.$video?.readyState >= 2 && this.trackProgress) {
      const progressUpdate = () => {
        this.percentageCompleted = (this.$video.currentTime / this.$video.duration) * 100
        if (!this.videoTwentyFive && this.percentageCompleted > 25) {
          this.videoTwentyFive = true
          this._handleTrackProgress('25%')
        }
        if (!this.videoFifty && this.percentageCompleted > 50) {
          this.videoFifty = true
          this._handleTrackProgress('50%')
        }
        if (!this.videoSeventyFive && this.percentageCompleted > 75) {
          this.videoSeventyFive = true
          this._handleTrackProgress('75%')
        }
        if (!this.videoComplete && this.percentageCompleted > 99) {
          this.videoComplete = true
          this._handleTrackProgress('100%')
          this.$video.removeEventListener('timeupdate', progressUpdate)
        }
      }
      this.$video.addEventListener('timeupdate', progressUpdate)
    }
  }

  _handleTrackProgress = (progress) => {
    // Track progress analytics event here
  }

  renderYoutubeVideo = () => {
    return html`
      <youtube-video
        ${ref(this.videoRef)}
        part="video-element"
        id="video-element"
        slot="media"
        src=${this.activeSrc}
        loading=${this.loadingType}
        muted
        playsinline
        aria-label=${this.description}
        ?autoplay=${this.autoplay}
        ?loop=${this.loop}
        @loadeddata=${this._handleProgress}
        @pause=${this._handlePause}
        @play=${this._handlePlay}
      >
        ${this.renderCaptions()}
      </youtube-video>
    `
  }

  renderVimeoVideo = () => {
    return html`
      <vimeo-video
        ${ref(this.videoRef)}
        part="video-element"
        id="video-element"
        slot="media"
        src=${this.activeSrc}
        loading=${this.loadingType}
        muted
        playsinline
        aria-label=${this.description}
        ?autoplay=${this.autoplay}
        ?loop=${this.loop}
        @loadeddata=${this._handleProgress}
        @pause=${this._handlePause}
        @play=${this._handlePlay}
      >
        ${this.renderCaptions()}
      </vimeo-video>
    `
  }

  renderUploadedVideo = () => {
    return html`
      <video
        ${ref(this.videoRef)}
        part="video-element"
        id="video-element"
        slot="media"
        src=${this.activeSrc}
        loading=${this.loadingType}
        muted
        playsinline
        aria-label=${this.description}
        ?autoplay=${this.autoplay}
        ?loop=${this.loop}
        @loadeddata=${this._handleProgress}
        @pause=${this._handlePause}
        @play=${this._handlePlay}
      >
        ${this.renderCaptions()}
      </video>
    `
  }

  renderCaptions = () => {
    if (this.captions) {
      return html`
        <track
          label="English"
          kind="subtitles"
          srclang="en"
          src="${window.routes?.domain}/cdn/shop/files/${this.captions.split('files/')[2]}"
        />`
    }
    return null
  }

  renderControls = () => {
    return html`
      <media-control-bar part="controls">
        ${this.sound ? html`
          <media-mute-button></media-mute-button>
        ` : ''}
        ${this.captions ? html`
          <media-captions-button></media-captions-button>
        ` : ''}
        <media-time-range></media-time-range>
        <media-time-display showduration remaining></media-time-display>
      </media-control-bar>
    `
  }

  play() {
    this.$video.play()
  }

  pause() {
    this.$video.pause()
  }

  renderHover() {
    return html`
      <div class="media-hover">
        ${!this.playStatus && (this.title || this.description) ? html`
          <div class="media-hover-content">
            ${this.title ? html`<h2>${this.title}</h2>` : ''}
            ${this.description ? html`<p>${this.description}</p>` : ''}
          </div>
        ` : ''}
        ${!this.autoplay ? html`
          <media-play-button></media-play-button>
        ` : ''}
      </div>
    `
  }

  render() {
    const stateClasses = {
      'background-video': this.background,
      'adapter-height': this.adaptContainerHeight,
    }

    return html`
      <media-controller class="${classMap(stateClasses)}" part="media-controller" style="--active-aspect-ratio: ${this.activeAspectRatio}">
        ${this.srcType === 'youtube' ? this.renderYoutubeVideo() : ''}
        ${this.srcType === 'vimeo' ? this.renderVimeoVideo() : ''}
        ${this.srcType === 'upload' ? this.renderUploadedVideo() : ''}
        ${this.activePoster ? html`
          <media-poster-image
            slot="poster"
            src=${this.activePoster}>
          </media-poster-image>` : ''}
        ${this.controls ? this.renderControls() : ''}
        ${this.renderHover()}
      </media-controller>
    `
  }

  static styles = [
    css`
      :host {
        display: block;
        height: 100%;
        width: 100%;
        --media-object-fit: cover;
      }
      #video-element {
        height: 100%;
        object-fit: cover;
        width: 100%;
      }
      media-controller {
        width: 100%;
        height: auto;
        aspect-ratio: var(--active-aspect-ratio);
        display: block;
      }
      .adapter-height {
        width: auto;
        height: 100%;
        left: 50%;
        transform: translateX(-50%);
      }
      media-control-bar {
        padding: 0 15px;
        position: absolute;
        width: 100%;
        bottom: 35px;
      }
      media-control-bar > * {
        cursor: pointer;
        --media-control-background: transparent;
        --media-control-hover-background: transparent;
        line-height: 0;
        padding: 0;
      }
      media-control-bar > *:last-child {
        margin: 0;
      }
      .background-video {
        height: 100%;
        width: auto;
        left: 50%;
        transform: translateX(-50%);
      }
      .media-hover {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        flex-direction: column;
        align-items: center;
      }
      .media-hover-content {
        color: var(--overlay-text-color);
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
      }
      .media-hover-content h2,
      .media-hover-content p {
        margin: 0;
      }
      media-play-button {
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: transparent;
      }
      @media screen and (min-width: 768px) {
        .background-video {
          position: relative;
          height: 100%;
          width: 100%;
          left: 50%;
          transform: translateX(-50%);
        }

        .background-video video {
          position: absolute;
          top: 0;
          left: 0;
        }
      }
      @media screen and (min-width: 990px) {
        media-control-bar {
          position: static;
        }
      }

      vimeo-video,
      youtube-video {
        min-width: unset;
        min-height: unset;
      }
    `,
  ]
}

customElements.define('hh-media-chrome-video', HHMediaChromeVideo)
